import React, { useState, useRef, Suspense, useEffect, useMemo } from "react";



import { useObjectStore } from "../zustand/objects";
import { useShallow } from "zustand/react/shallow";
import {
  VIEWPORT_SHADOW_MAP,
  VIEWPORT_SKY_ATMOSPHERE,
  getEditorGridLineColors,
  getViewportSceneTheme,
  getViewportCappedDpr,
} from "../utils/viewportCanvasDefaults";
// react three fiber takes care of camera position in canvas
import { Canvas, useThree } from "@react-three/fiber";
import { XR, Controllers, useXREvent } from "@react-three/xr";
import { XrInvalidateWhilePresenting } from "./XrInvalidateWhilePresenting";
// drei have lots of shapes without writing code
import { OrbitControls, BakeShadows } from "@react-three/drei";
import { AtmosphereSky } from "./AtmosphereSky";
import { AnimatedSceneBackground } from "./AnimatedSceneBackground";
import { ViewportSunLights } from "./ViewportSunLights";
import { AnimatedFloorMaterial } from "./AnimatedFloorMaterial";
import { AnimatedGridHelper } from "./AnimatedGridHelper";
import { Loader } from "@mantine/core"
import { ObjectsMapped } from "./ObjectsMapped";
import CommentsMapped from "./CommentsMapped";

import Cursor from "./Cursor";
import PlayerFigure from "./PlayerFigure";
import CaptureCursor from "./CaptureCursor";
import { GroupedObjectsMapped } from "./GroupedObjectsMapped";
import { VRTeleportFloorGrid } from './TeleportFloorGrid'
import {CommentCursor} from "./CommentCursor"

const Viewport = ({ 
  objectDetails,
  undoing,
  artboards,
  projectKey,
  presence
}) => {
  const orbitControls = useRef();
  
  const {
    objectsAreLoaded,
    commentClickStatus,
    showingComments,
    currentObjectArtboard,
    floorColour,
    viewportThemeOverride,
    groupedObjects,
    transformMode,
    isMovingVRObject,
    editorCameraPreset,
    editorCameraPresetNonce,
  } = useObjectStore(
    useShallow((s) => ({
      objectsAreLoaded: s.objectsAreLoaded,
      commentClickStatus: s.commentClickStatus,
      showingComments: s.showingComments,
      currentObjectArtboard: s.currentObjectArtboard,
      floorColour: s.floorColour,
      viewportThemeOverride: s.viewportThemeOverride,
      groupedObjects: s.groupedObjects,
      transformMode: s.transformMode,
      isMovingVRObject: s.isMovingVRObject,
      editorCameraPreset: s.editorCameraPreset,
      editorCameraPresetNonce: s.editorCameraPresetNonce,
    }))
  );

  const isFigmaCaptureMode =
    typeof window !== "undefined" &&
    typeof window.location?.hash === "string" &&
    window.location.hash.includes("figmacapture=");
  
  const COLORS = [
    "#E57373",
    "#9575CD",
    "#4FC3F7",
    "#81C784",
    "#FFF176", 
    "#FF8A65",
    "#F06292",
    "#7986CB",
  ];

  // we need to map the others in the room up here somewhere in an array
  // then we need to subscribe each cursor to the positions, that can change,
  // potentially put these cursor positions in zustand, 
  // so changes can be fed to the mapped cursors without rerendering them
  // that way, we might be able to animate the transition between cursor positions

  // used to be from liveblocksStore


  // - - - -  cursors end


  const canvasRef = useRef()
  // Keeps `VIEWPORT_SKY_ATMOSPHERE` in this module’s scope (avoids mixed HMR chunks / stale `<Sky {...} />` refs).
  const _viewportSkyAtmosphereCompat = useRef(VIEWPORT_SKY_ATMOSPHERE)

  const dpr = getViewportCappedDpr();

  // - - - -  cursors end

  // vr stuff start
  const [showTiles, setShowTiles] = useState(false);

  const changeTiles = (e) => setShowTiles(e)

  // vr stuff end

  const sceneTheme = getViewportSceneTheme(viewportThemeOverride);
  const editorCanvasBackground = sceneTheme.background;
  const editorGridColors = useMemo(
    () => getEditorGridLineColors(floorColour, sceneTheme.mode),
    [floorColour, sceneTheme.mode]
  );

  if (!objectsAreLoaded) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader color="black" />
      </div>
    );
    // return <div>Loading...</div>;
  } else {
    return (
      <Suspense fallback={null}>
        {/* <VRButton sessionInit={{ optionalFeatures: ['hand-tracking'] }} /> */}
        <Canvas
        id="viewport_canvas"
          shadows
          camera={{
            position: [0, 10, 10],
            fov: 25
          }} 
          gl={isFigmaCaptureMode ? { preserveDrawingBuffer: true } : undefined}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "block",
          }} 
          alpha="false"
          dpr={dpr}
          frameloop="demand"
          onCreated={(state) => {
            state.invalidate();
          }}
          ref={canvasRef}>
            {commentClickStatus.clicking && <CommentCursor projectKey={projectKey} threadsId={commentClickStatus.threadsId} artboard={commentClickStatus.artboard}/>}
            <XR>
            <XrInvalidateWhilePresenting />
            {/* <TeleportationPlane leftHand={false} rightHand={true} maxDistance={10} size={0.5}/>  */}

            {/* <CaptureHeadset currentObjectArtboard={currentObjectArtboard}/> */}
          <AnimatedSceneBackground color={editorCanvasBackground} />
          <AtmosphereSky {...sceneTheme.skyAtmosphere} />
          <ViewportSunLights
            ambientIntensity={sceneTheme.ambientIntensity}
            directionalIntensity={sceneTheme.directionalIntensity}
            directionalColor={sceneTheme.directionalColor}
            shadowMapSize={VIEWPORT_SHADOW_MAP}
          />
            
          <OrbitControls
            dampingFactor={1}
            keyPanSpeed={1500}
            zoomSpeed={1}
            maxDistance={100}
            minDistance={1}
            enableZoom={true}
            enableDamping={true}
            enablePan={true}
            screenSpacePanning={true}
            ref={orbitControls}
            makeDefault
          />
          <CameraPresetApplier
            orbitControlsRef={orbitControls}
            preset={editorCameraPreset}
            presetNonce={editorCameraPresetNonce}
          />
            <Controllers rayMaterial={{ color: 'blue' }} />
          <group>
            <BakeShadows />
            <Suspense fallback={null}>
              <mesh
                receiveShadow
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0, 0]}
              // onPointerMove={(e) => handlePointerMoveSet({cameraPosition: new THREE.Vector3().unproject(e.camera), point: e.point, lookAt: e.ray.direction})}
              // onClick={() => missedShape()}
              >
                <planeGeometry attach="geometry" args={[50, 50]} />
                <AnimatedFloorMaterial
                  color={floorColour}
                  opacity={1}
                  receiveShadow
                />
              </mesh>
            </Suspense>
            {!isMovingVRObject && <SetTilesFunction changeTiles={changeTiles}/>}
            { showTiles ? <VRTeleportFloorGrid /> : ''}
           <Suspense fallback={null}>
              <ObjectsMapped
                artboards={artboards}
                orbitControls={orbitControls}
                objectDetails={objectDetails}
                currentObjectArtboard={currentObjectArtboard}
                objectsAreLoaded={objectsAreLoaded}
                undoing={undoing}
              />
            </Suspense>

            {showingComments && <CommentsMapped 
            currentObjectArtboard={currentObjectArtboard}
            artboards={artboards}
            orbitControls={orbitControls}
            />}

            {groupedObjects.length >= 1 && <Suspense fallback={null}>
              <GroupedObjectsMapped
                artboards={artboards}
                orbitControls={orbitControls}
                objectDetails={objectDetails} 
                currentObjectArtboard={currentObjectArtboard}
                objectsAreLoaded={objectsAreLoaded}
                undoing={undoing}
              />
            </Suspense>}
 
             
            
            {/* <Person/> */}
           
          </group>
          <AnimatedGridHelper
            scale={0.5}
            position={[0, 0, 0]}
            size={100}
            divisions={100}
            centerLineColor={editorGridColors.centerLine}
            gridColor={editorGridColors.grid}
          />
        <CursorsAndHeadsets presence={presence} currentObjectArtboard={currentObjectArtboard} COLORS={COLORS} transformMode={transformMode} projectKey={projectKey}/>
         
        </XR>
        </Canvas>
      </Suspense>

    );
  }
};

const CameraPresetApplier = ({ orbitControlsRef, preset, presetNonce }) => {
  const invalidate = useThree((s) => s.invalidate);
  /** `makeDefault` registers OrbitControls here; the forwarded ref can lag behind. */
  const controlsFromStore = useThree((s) => s.controls);

  useEffect(() => {
    const PRESETS = {
      perspective: { position: [0, 10, 10], target: [0, 0, 0] },
      top: { position: [0, 20, 0.01], target: [0, 0, 0] },
      zoom: { position: [0, 6, 6], target: [0, 0, 0] },
    };

    const cfg = PRESETS[preset] || PRESETS.perspective;
    const durationMs = 450;
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    let rafId = null;
    let mountWaitRafId = null;
    let cancelled = false;
    let restoreDamping = null;

    const resolveOrbit = () => controlsFromStore ?? orbitControlsRef?.current;

    const runTween = (orbit) => {
      const prevDamping = orbit.enableDamping;
      orbit.enableDamping = false;
      restoreDamping = () => {
        orbit.enableDamping = prevDamping;
        orbit.update();
        restoreDamping = null;
      };

      const startPos = orbit.object.position.clone();
      const startTarget = orbit.target.clone();

      const endPos = new (startPos.constructor)(cfg.position[0], cfg.position[1], cfg.position[2]);
      const endTarget = new (startTarget.constructor)(cfg.target[0], cfg.target[1], cfg.target[2]);

      const startTs = performance.now();
      const step = (now) => {
        if (cancelled) return;

        const elapsed = now - startTs;
        const t = Math.min(1, elapsed / durationMs);
        const eased = easeInOutCubic(t);

        orbit.object.position.lerpVectors(startPos, endPos, eased);
        orbit.target.lerpVectors(startTarget, endTarget, eased);
        orbit.update();
        invalidate();

        if (t < 1) {
          rafId = requestAnimationFrame(step);
        } else if (restoreDamping) {
          restoreDamping();
          invalidate();
        }
      };

      rafId = requestAnimationFrame(step);
    };

    let waitFrames = 0;
    const waitForOrbitAndApply = () => {
      if (cancelled) return;
      if (++waitFrames > 600) return;
      const orbit = resolveOrbit();
      if (orbit?.object && orbit?.target) {
        runTween(orbit);
        return;
      }
      mountWaitRafId = requestAnimationFrame(waitForOrbitAndApply);
    };

    mountWaitRafId = requestAnimationFrame(waitForOrbitAndApply);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (mountWaitRafId) cancelAnimationFrame(mountWaitRafId);
      if (restoreDamping) restoreDamping();
    };
  }, [preset, presetNonce, invalidate, orbitControlsRef, controlsFromStore]);

  return null;
};

export default Viewport;

const CursorsAndHeadsets = ({presence, currentObjectArtboard, COLORS, transformMode, projectKey}) => {
  const localSocketId = useObjectStore((s) => s.realtimeClientSocketId);
  const rowForProject = (row) =>
    row && (projectKey == null || String(row.projectKey) === String(projectKey));

  return (
    <>
    {/* CURSORS */}
      <Suspense fallback={null}>
        {Object.keys(presence).length > 0 && Object.keys(presence).map((mapped) =>
          rowForProject(presence[mapped]) && presence[mapped].update.cursor && presence[mapped].update.cursor.page === 'editor' && String(presence[mapped].update.cursor.artboard) === String(currentObjectArtboard) && <Cursor key={mapped} rotation={presence[mapped].update.cursor.rotation} position={presence[mapped].update.cursor.position} color={COLORS[mapped.match(/\d+/)[0] % COLORS.length]} mouse={presence[mapped].update.cursor.mouse} />
        )}</Suspense>
        {/* DESKTOP HEADSETS */}
      <Suspense fallback={null}>
        {Object.keys(presence).length > 0 && Object.keys(presence).map((mapped) =>
          rowForProject(presence[mapped]) && presence[mapped].update.preview && presence[mapped].update.preview.page === 'preview' && String(presence[mapped].update.preview.artboard) === String(currentObjectArtboard) && mapped !== localSocketId && <PlayerFigure key={mapped} rotation={presence[mapped].update.preview.rotation} position={presence[mapped].update.preview.position} color={COLORS[mapped.match(/\d+/)[0] % COLORS.length]} device={presence[mapped].update.preview.device} />
        )}</Suspense>
        {/* HEADSETS */}
        {/* CAPTURING EDITOR CURSOR */}
        {Object.keys(presence).length > 0 && transformMode !== 'scale' && <CaptureCursor currentObjectArtboard={currentObjectArtboard}/>}
    </>
  )
}

const SetTilesFunction = ({changeTiles}) => {
  
 let timer

const handleLongPress = () => {
  timer = setTimeout(() => changeTiles(true), 900)
}

const handleButtonRelease = () => {
  clearTimeout(timer)
  changeTiles(false)
}
useXREvent('selectstart', handleLongPress)
useXREvent('selectend', handleButtonRelease)
}


export function Person() {

  // const url = "https://figma-alpha-api.s3.us-west-2.amazonaws.com/images/730a12e6-9bf3-4306-a4ca-dbe8db042b39"

  
  // const texture = useTexture(url)

   
  return (<>
     {/* <LargeHall/> */}
     {/* <LShapedRoom/> */}
     {/* <WithWindowSquareRoom/> */}
     {/* <Hallway/> */}
     {/* <NoWindowSquareRoom/> */}
      </>
);
}

